import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import supabase from '../supabase';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import '../design-system.css';

function CreatorProfile() {
    const { username } = useParams();
    const navigate = useNavigate();
    const [creator, setCreator] = useState(null);
    const [albums, setAlbums] = useState([]);
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            const { data: creatorsData } = await supabase.rpc('get_public_creators');
            const userData = creatorsData?.find(u => u.username === username);
            if (!userData) { setLoading(false); return; }
            setCreator(userData);

            const fullName = `${userData.first_name || ''} ${userData.last_name || ''}`.trim();
            const { data: albumData } = await supabase
                .from('albums')
                .select('*')
                .eq('created_by', fullName)
                .eq('archived', false)
                .order('date', { ascending: false });
            if (albumData) setAlbums(albumData);

            const { data: postsData } = await supabase
                .from('posts')
                .select('*')
                .eq('author_username', username)
                .order('created_at', { ascending: false });
            if (postsData) setPosts(postsData);

            setLoading(false);
        };
        fetchData();
    }, [username]);

    if (loading) {
        return (
            <div className="w-page">
                <Navbar variant="light" />
                <div className="w-body" style={{ textAlign: 'center', color: 'var(--w-muted)', fontSize: '13px' }}>Loading...</div>
                <Footer variant="light" />
            </div>
        );
    }

    if (!creator) {
        return (
            <div className="w-page">
                <Navbar variant="light" />
                <div className="w-body" style={{ textAlign: 'center', color: 'var(--w-muted)', fontSize: '13px' }}>Creator not found.</div>
                <Footer variant="light" />
            </div>
        );
    }

    const fullName = `${creator.first_name || ''} ${creator.last_name || ''}`.trim() || creator.username;
    const websiteHref = creator.website_url
        ? (creator.website_url.startsWith('http') ? creator.website_url : `https://${creator.website_url}`)
        : '';
    const websiteLabel = creator.website_url
        ? creator.website_url.replace(/^https?:\/\//, '')
        : '';

    return (
        <div className="w-page">
            <Navbar variant="light" />

            <div className="w-body">

                {/* Bandeau + ligne avatar — même card, pas de chevauchement */}
                <div className="w-profile-header">
                    <div className="w-profile-banner-strip" style={{ aspectRatio: '4 / 1', minHeight: '160px' }}>
                        {creator.banner_url && <img src={creator.banner_url} alt="banner" />}
                    </div>

                    <div className="w-profile-row">
                        <div className="w-profile-avatar-sq" style={{ width: '64px', height: '64px', fontSize: '20px' }}>
                            {creator.avatar_url ? (
                                <img src={creator.avatar_url} alt={fullName} />
                            ) : (
                                creator.username?.slice(0, 2).toUpperCase()
                            )}
                        </div>

                        <div className="w-profile-row-name">
                            <p style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '16px' }}>
                                {fullName}
                                {creator.is_verified && (
                                    <span title="Compte officiel vérifié" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '16px', height: '16px', borderRadius: '50%', background: 'var(--w-ink)', flexShrink: 0 }}>
                                        <svg width="9" height="9" viewBox="0 0 12 12" fill="none">
                                            <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </span>
                                )}
                            </p>
                            <p>@{creator.username}</p>
                        </div>
                    </div>
                </div>

                {/* Stats */}
                <div className="w-stats">
                    <div className="w-stat-card">
                        <div className="w-stat-num">{albums.length}</div>
                        <div className="w-stat-label">Album{albums.length !== 1 ? 's' : ''}</div>
                    </div>
                    <div className="w-stat-card">
                        <div className="w-stat-num">{posts.length}</div>
                        <div className="w-stat-label">Post{posts.length !== 1 ? 's' : ''}</div>
                    </div>
                </div>

                {/* Bio + lien */}
                {(creator.bio || creator.website_url) && (
                    <div style={{ marginBottom: '40px', maxWidth: '520px' }}>
                        {creator.bio && (
                            <p style={{ fontSize: '14px', color: 'var(--w-ink-soft)', lineHeight: 1.7, margin: '0 0 10px' }}>
                                {creator.bio}
                            </p>
                        )}
                        {creator.website_url && (
                            <a href={websiteHref} target="_blank" rel="noreferrer" style={{ fontSize: '13px', color: 'var(--w-accent)', textDecoration: 'none', fontWeight: 500 }}>
                                🔗 {websiteLabel}
                            </a>
                        )}
                    </div>
                )}

                {/* Posts */}
                {posts.length > 0 && (
                    <>
                        <div className="w-section-head">
                            <span className="w-eyebrow" style={{ margin: 0 }}>Posts · {posts.length}</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '48px' }}>
                            {posts.map(post => (
                                <div
                                    key={post.slug}
                                    className="w-inline-card"
                                    style={{ maxWidth: 'none' }}
                                    onClick={() => navigate(`/blog/${post.slug}`)}
                                >
                                    <div>
                                        <p style={{ fontSize: '13.5px', fontWeight: 600, margin: '0 0 4px', color: 'var(--w-ink)' }}>
                                            {post.title}
                                            {post.is_statement && <span style={{ marginLeft: 8, fontSize: '9px', color: 'var(--w-accent)', letterSpacing: '0.04em' }}>● OFFICIAL</span>}
                                        </p>
                                        <p style={{ fontSize: '11.5px', color: 'var(--w-muted)', margin: 0 }}>
                                            {new Date(post.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                        </p>
                                    </div>
                                    <span style={{ fontSize: '12px', color: 'var(--w-muted)' }}>→</span>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {/* Albums */}
                <div className="w-section-head">
                    <span className="w-eyebrow" style={{ margin: 0 }}>Albums · {albums.length}</span>
                </div>

                {albums.length === 0 ? (
                    <p className="w-subtitle">No albums yet.</p>
                ) : (
                    <div className="w-album-grid">
                        {albums.map(album => (
                            <div key={album.folder} className="w-album-card" onClick={() => navigate(`/region/${album.region}/${album.folder}`)}>
                                <div className="w-album-cover">
                                    {album.cover_url && <img src={album.cover_url} alt={album.title} />}
                                </div>
                                <div className="w-album-info">
                                    <div style={{ minWidth: 0 }}>
                                        <div className="w-album-title">{album.title}</div>
                                        <div className="w-album-meta">{album.location || album.region}{album.date ? ` · ${album.date}` : ''}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <Footer variant="light" />
        </div>
    );
}

export default CreatorProfile;